<template>
  <b-modal
    ref="modal"
    :title="modalTitle"
    centered
    size="lg"
    no-close-on-backdrop
    @hide="closeHandler"
    @shown="handleModalShown"
  >
    <!-- Publishing Form -->
    <b-card class="mb-3">
      <b-form>
        <b-row>
          <b-col cols="12">
            <b-form-group
              label="Platform"
              label-for="platform-select"
              label-class="mb-1"
            >
              <b-form-select
                id="platform-select"
                v-model="platformId"
                size="sm"
                @change="handlePlatformChange"
              >
                <b-form-select-option
                  value=""
                  disabled
                  selected
                >
                  Select Platform
                </b-form-select-option>
                <b-form-select-option
                  v-for="platform in platforms"
                  :key="platform.id"
                  :value="platform.id"
                >
                  {{ platform.name }}
                </b-form-select-option>
              </b-form-select>
            </b-form-group>
          </b-col>
        </b-row>

        <b-row v-if="selectedItems && selectedItems.length > 0">
          <b-col cols="12">
            <b-form-group
              label="Selected file names"
              label-class="mb-1"
            >
              <div
                v-for="(item, index) in selectedItems"
                :key="item.id"
                class="mb-2"
              >
                <b-form-input
                  :id="`file-name-${index}`"
                  v-model="item.name"
                  size="sm"
                  :placeholder="`File ${index + 1} name`"
                />
              </div>
            </b-form-group>
          </b-col>
        </b-row>
      </b-form>
    </b-card>

    <!-- Success Results Block -->
    <b-card
      v-if="(salsifyPublishResults && salsifyPublishResults.length > 0) || (bynderPublishResults && bynderPublishResults.length > 0) || (walmartPublishResults && walmartPublishResults.length > 0)"
      class="mb-3 border-success"
    >
      <div class="d-flex align-items-center mb-3">
        <h6 class="mb-0 text-success">
          {{ successMessage }}
        </h6>
      </div>

      <div
        v-if="isSalsify"
        class="published-files"
      >
        <div
          v-for="result in salsifyPublishResults"
          :key="result.file_id"
          class="published-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ result.file_name }}</strong>
              <div class="text-muted small">
                File ID: {{ result.file_id }}
              </div>
              <div
                v-if="result.media_id"
                class="text-muted small"
              >
                Asset ID: {{ result.media_id }}
              </div>
            </div>
            <div>
              <a
                v-if="result.url"
                :href="result.url"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fas fa-external-link-alt mr-1" />
                View on Salsify
              </a>
              <span
                v-else
                class="badge badge-success"
              >
                Published
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isBynder"
        class="published-files"
      >
        <div
          v-for="result in bynderPublishResults"
          :key="result.file_id"
          class="published-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ result.file_name }}</strong>
              <div class="text-muted small">
                File ID: {{ result.file_id }}
              </div>
              <div
                v-if="result.media_id"
                class="text-muted small"
              >
                Asset ID: {{ result.media_id }}
              </div>
            </div>
            <div>
              <span class="badge badge-success">
                Published
              </span>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="isWalmart"
        class="published-files"
      >
        <div
          v-for="result in walmartPublishResults"
          :key="result.file_id"
          class="published-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-center">
            <div>
              <strong>{{ result.file_name }}</strong>
              <div class="text-muted small">
                File ID: {{ result.file_id }}
              </div>
              <div
                v-if="result.message"
                class="text-success small mt-1"
              >
                {{ result.message }}
              </div>
            </div>
            <div>
              <span class="badge badge-success">
                Published
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <!-- Error Results Block -->
    <b-card
      v-if="publishErrors && publishErrors.length > 0"
      class="mb-3 border-danger"
    >
      <div class="d-flex align-items-center mb-3">
        <h6 class="mb-0 text-danger">
          Publishing Errors
        </h6>
      </div>

      <div class="error-files">
        <div
          v-for="error in publishErrors"
          :key="error.file_id"
          class="error-file-item mb-2 p-2 bg-light rounded"
        >
          <div class="d-flex justify-content-between align-items-start">
            <div class="flex-grow-1">
              <strong>{{ error.file_name }}</strong>
              <div class="text-muted small">
                File ID: {{ error.file_id }}
              </div>
              <div
                v-if="error.media_id"
                class="text-muted small"
              >
                Asset ID: {{ error.media_id }}
              </div>
              <div class="text-danger small mt-1">
                {{ error.error_message }}
              </div>
            </div>
            <div>
              <a
                v-if="error.url"
                :href="error.url"
                target="_blank"
                class="btn btn-sm btn-outline-primary"
              >
                <i class="fas fa-external-link-alt mr-1" />
                View on Salsify
              </a>
              <span
                v-else
                class="badge badge-danger"
              >
                Failed
              </span>
            </div>
          </div>
        </div>
      </div>
    </b-card>

    <template #modal-footer>
      <div class="w-100 d-flex justify-content-end">
        <b-button
          class="mr-2"
          variant="outline-secondary"
          @click="$refs.modal.hide()"
        >
          {{
            (salsifyPublishResults && salsifyPublishResults.length > 0) ||
              (bynderPublishResults && bynderPublishResults.length > 0) ||
              (walmartPublishResults && walmartPublishResults.length > 0)
              ? 'Close'
              : 'Cancel'
          }}
        </b-button>
        <b-button
          v-if="isSalsify"
          variant="primary"
          :disabled="isPublishing || !platformId"
          @click="handleSalsifyPublish"
        >
          <b-spinner
            v-if="isPublishing"
            small
            class="mr-1"
          />
          {{ isPublishing ? actioningText : actionButtonText('Salsify') }}
        </b-button>
        <b-button
          v-if="isBynder"
          variant="primary"
          :disabled="isPublishing || !platformId"
          @click="handleBynderPublish"
        >
          <b-spinner
            v-if="isPublishing"
            small
            class="mr-1"
          />
          {{ isPublishing ? actioningText : actionButtonText('Bynder') }}
        </b-button>
        <b-button
          v-if="isWalmart"
          variant="primary"
          :disabled="isPublishing || !platformId"
          @click="handleWalmartPublish"
        >
          <b-spinner
            v-if="isPublishing"
            small
            class="mr-1"
          />
          {{ isPublishing ? actioningText : actionButtonText('Walmart') }}
        </b-button>
      </div>
    </template>
  </b-modal>
</template>

<script>
import mixinModal from './mixin';
import { mapGetters } from 'vuex';
import { bynderEventBroker, BYNDER_EVENTS } from '@frontend/group/modules/bynder/event-broker';
import { salsifyEventBroker, SALSIFY_EVENTS } from '@frontend/group/modules/salsify/event-broker';

export default {
  name: "PublishToPlatformModal",
  mixins: [mixinModal],
  data: () => ({
    isPublishing: false,
    platformId: '',
    platforms: [],
    salsifyPublishResults: [],
    publishErrors: [],
    isSalsify: false,
    isBynder: false,
    isWalmart: false,
    publishInBackground: false,
    bynderPublishResults: [],
    walmartPublishResults: [],
    platformsPayload: [],
    publishToBynder: true,
    publishToSalsify: true,
    publishToWalmart: true,
    walmartTypes: ['mp4', 'mov'],
  }),
  computed: {
    ...mapGetters('fileBrowser', [
      'selectedItems',
      'bynderAvailable',
      'salsifyAvailable',
      // 'isPublishToBynder',
      'firstSelectedItem',
    ]),
    modalTitle() {
      return this.isCopyMode ? 'Copy to Platform' : 'Publish to Platform';
    },
    successMessage() {
      return this.isCopyMode ? 'Successfully Copied to Platform' : 'Successfully Published to Platform';
    },
    actioningText() {
      return this.isCopyMode ? 'Copying...' : 'Publishing...';
    },
    isCopyMode() {
      return !this.publishToBynder || !this.publishToSalsify || !this.publishToWalmart;
    }
  },
  mounted() {
    bynderEventBroker.on(BYNDER_EVENTS.HANDLE_COPY_TO_BYNDER, () => {
      this.publishToBynder = true;
      this.publishToSalsify = false;
      this.publishToWalmart = false;
    });

    salsifyEventBroker.on(SALSIFY_EVENTS.HANDLE_COPY_TO_SALSIFY, () => {
      this.publishToBynder = false;
      this.publishToSalsify = true;
      this.publishToWalmart = false;
    });
  },
  methods: {
    handleModalShown() {
      this.getPlatforms();
    },

    async getPlatforms() {
      try {
        this.platformsPayload = [];

        if (this.bynderAvailable && this.publishToBynder) {
          this.platformsPayload.push('bynder');
        }

        if (this.salsifyAvailable && this.publishToSalsify) {
          this.platformsPayload.push('salsify');
        }

        if (this.walmartTypes.includes(this.firstSelectedItem.type) && this.selectedItems.length === 1 && this.publishToWalmart) {
          this.platformsPayload.push('walmart');
        }

        const { data } = await axios.post('/platforms/get-platforms', { platforms: this.platformsPayload });

        this.platforms = data.platforms || [];
      } catch (error) {
        console.error('Error fetching platforms:', error);
        toastr.error('Failed to load platforms');
      }
    },

    handlePlatformChange() {
      this.salsifyPublishResults = [];
      this.bynderPublishResults = [];
      this.walmartPublishResults = [];
      this.publishErrors = [];

      const selectedPlatform = this.platforms.find(platform => platform.id == this.platformId);

      if (selectedPlatform) {
        const platformName = selectedPlatform.platform;

        switch (platformName) {
          case 'bynder':
            this.isBynder = true;
            this.isSalsify = false;
            this.isWalmart = false;
            break;
          case 'salsify':
            this.isSalsify = true;
            this.isBynder = false;
            this.isWalmart = false;
            break;
          case 'walmart':
            this.isWalmart = true;
            this.isBynder = false;
            this.isSalsify = false;
            break;
          default:
            this.isBynder = false;
            this.isSalsify = false;
            this.isWalmart = false;
            break;
        }
      }
    },

    validatePublishData() {
      if (!this.platformId) {
        toastr.error('Please select a platform.');
        return false;
      }

      if (!this.selectedItems || this.selectedItems.length === 0) {
        toastr.error('No files selected for publishing.');
        return false;
      }

      return true;
    },

    collectPublishData() {
      return {
        platform_id: this.platformId,
        files: this.selectedItems.map(item => ({
          id: item.id,
          name: item.name,
          url: item.path,
          original_path: item.original_path,
          asset_type: item?.asset_type,
          extension: item?.extension,
        }))
      };
    },

    async handleSalsifyPublish() {
      if (!this.validatePublishData()) {
        return;
      }

      this.isPublishing = true;

      this.salsifyPublishResults = [];
      this.bynderPublishResults = [];
      this.walmartPublishResults = [];
      this.publishErrors = [];

      const publishData = this.collectPublishData();

      try {
        const { data } = await axios.post('/salsify/publish-assets', publishData);

        if (data.status === 'completed' && Array.isArray(data.results)) {
          data.results.forEach(result => {
            if (result.status === 'success') {
              this.salsifyPublishResults.push({
                file_id: result.file_id,
                file_name: result.file_name,
                url: result.url || null
              });
            } else if (result.status === 'error') {
              this.publishErrors.push({
                file_id: result.file_id,
                file_name: result.file_name,
                error_message: result.error || 'Publishing failed',
                url: result.url || null
              });
            }
          });

          const successCount = this.salsifyPublishResults.length;
          const errorCount = this.publishErrors.length;

          if (successCount > 0 && errorCount === 0) {
            toastr.success(`${successCount} file(s) published successfully to ${this.source}!`);
          } else if (successCount > 0 && errorCount > 0) {
            toastr.warning(`${successCount} file(s) published successfully, ${errorCount} failed. Check details below.`);
          } else if (errorCount > 0) {
            toastr.error(`Failed to publish ${errorCount} file(s). Check details below.`);
          }
        } else if (data.status === 'error') {
          toastr.error(data.message);

          this.publishErrors = this.selectedItems.map(item => ({
            file_id: item.id,
            file_name: item.name,
            error_message: data.message || 'Publishing failed'
          }));
        }
      } catch (error) {
        console.error(`Error publishing to ${this.source}:`, error);
        toastr.error(`Failed to publish assets to ${this.source}`);

        this.publishErrors = this.selectedItems.map(item => ({
          file_id: item.id,
          file_name: item.name,
          error_message: error.response?.data?.message || 'Network or server error occurred'
        }));
      } finally {
        this.isPublishing = false;
      }
    },

    async handleBynderPublish() {
      if (!this.validatePublishData()) {
        return;
      }

      this.salsifyPublishResults = [];
      this.bynderPublishResults = [];
      this.walmartPublishResults = [];
      this.publishErrors = [];

      this.isPublishing = true;

      if (this.publishInBackground) {
        // todo: add background publish
        // skip for now
        // const { data } = await axios.post('/bynder/publish-assets', {
        //   publish_in_background: true,
        //   platform_id: this.platformId,
        //   files: this.selectedItems.map(item => ({
        //     id: item.id,
        //     name: item.name,
        //     url: item.path,
        //   }))
        // });

        // console.log('data', data);
      }

      if (!this.publishInBackground) {
        try {
          for (const item of this.selectedItems) {
            try {
              const { data } = await axios.post('/bynder/publish-asset', {
                publish_in_background: false,
                platform_id: this.platformId,
                file: {
                  id: item.id,
                  name: item.name,
                  url: item.path,
                  original_path: item?.original_path,
                  asset_type: item?.asset_type,
                  extension: item?.extension,
                }
              });

              if (data.status === 'success') {
                this.bynderPublishResults.push({
                  file_id: item.id,
                  file_name: item.name,
                  media_id: data.media_id,
                });
              } else {
                this.publishErrors.push({
                  file_id: item.id,
                  file_name: item.name,
                  error_message: data.message || 'Publishing failed'
                });
              }
            } catch (error) {
              console.error(`Error publishing to ${this.source}:`, error);
              toastr.error(`Failed to publish assets to ${this.source}`);
            }
          }
        } catch (error) {
          console.error(`Error publishing to ${this.source}:`, error);
          toastr.error(`Failed to publish assets to ${this.source}`);
        } finally {
          this.isPublishing = false;
        }
      }
    },

    async handleWalmartPublish() {
      if (!this.platformId) {
        toastr.error('Please select a platform.');
        return;
      }

      this.isPublishing = true;

      this.salsifyPublishResults = [];
      this.bynderPublishResults = [];
      this.walmartPublishResults = [];
      this.publishErrors = [];

      const publishData = this.collectPublishData();

      try {
        const { data } = await axios.post('/walmart/upload-media', publishData);

        if (data.status === 'success') {
          const fileData = publishData.files[0];

          this.walmartPublishResults.push({
            file_id: fileData.id,
            file_name: fileData.name,
            message: data.message,
            url: null
          });

          toastr.success(data.message);
        } else if (data.status === 'error') {
          toastr.error(data.message);

          this.publishErrors = this.selectedItems.map(item => ({
            file_id: item.id,
            file_name: item.name,
            error_message: data.message || 'Publishing failed'
          }));
        }
      } catch (error) {
        console.error('Error publishing to Walmart:', error);
        toastr.error('Failed to publish assets to Walmart');

        this.publishErrors = this.selectedItems.map(item => ({
          file_id: item.id,
          file_name: item.name,
          error_message: error.response?.data?.message || 'Network or server error occurred'
        }));
      } finally {
        this.isPublishing = false;
      }
    },

    actionButtonText(platform) {
      return this.isCopyMode ? `Copy to ${platform}` : `Publish to ${platform}`;
    },

    resetModal() {
      this.isPublishing = false;
      this.platformId = '';
      this.platforms = [];
      this.salsifyPublishResults = [];
      this.bynderPublishResults = [];
      this.walmartPublishResults = [];
      this.publishErrors = [];
      this.publishToBynder = true;
      this.publishToSalsify = true;
      this.publishToWalmart = true;
    },

    closeHandler() {
      this.resetModal();
      this.$emit('close');
    }
  }
}
</script>

<style scoped>
.published-file-item {
  border-left: 3px solid #28a745;
}

.published-files {
  max-height: 300px;
  overflow-y: auto;
}

.error-file-item {
  border-left: 3px solid #dc3545;
}

.error-files {
  max-height: 300px;
  overflow-y: auto;
}
</style>
